import { useMemo } from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { describeEventType } from './eventCatalogue';

const { Text } = Typography;

interface CatalogueRow {
  type: string;
  group: string | null;
  meaning: string | null;
}

/**
 * The event-catalogue browser: the SERVER's closed subscribable list (the authority), each type
 * with its plain-language meaning so an implementer can see what is subscribable before wiring a
 * receiver. A type this console has no words for renders honestly undescribed — the id stays
 * authoritative, nothing is invented.
 */
export function EventCatalogueTable({ types }: { types: string[] }) {
  const rows = useMemo<CatalogueRow[]>(
    () =>
      types.map((type) => {
        const d = describeEventType(type);
        return { type, group: d?.group ?? null, meaning: d?.meaning ?? null };
      }),
    [types],
  );

  const columns: ColumnsType<CatalogueRow> = [
    {
      title: 'Event type',
      dataIndex: 'type',
      key: 'type',
      width: 320,
      render: (v: string) => (
        <span className="ol-mono" style={{ fontSize: 12 }}>
          {v}
        </span>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'group',
      key: 'group',
      width: 160,
      render: (v: string | null) =>
        v ? (
          <Tag bordered={false} style={{ fontSize: 11 }}>
            {v}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'What it means',
      dataIndex: 'meaning',
      key: 'meaning',
      render: (v: string | null) =>
        v ? (
          <Text style={{ fontSize: 12.5 }}>{v}</Text>
        ) : (
          <Text type="secondary" italic style={{ fontSize: 12.5 }}>
            No description in this console yet — the id is authoritative.
          </Text>
        ),
    },
  ];

  return (
    <Table<CatalogueRow>
      rowKey="type"
      size="small"
      columns={columns}
      dataSource={rows}
      pagination={false}
      locale={{ emptyText: 'The server returned no subscribable event types.' }}
    />
  );
}
